import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, finalize, Subscription } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { AppGlobalControlValues } from 'src/app/common/app.global.control.values';
import { promiseWithCatch, unsubscribeAll } from 'src/app/common/app.helpers';
import { OperationInfoService } from 'src/app/common/long-operations/operation-info.service';
import ProjectLinks from 'src/app/common/project.links';
import { PaginationResolver } from 'src/app/interfaces/pagination.interface';
import { TableCellOnActionEvent, TableConstructorData } from 'src/app/interfaces/tables.interfaces';
import { ItemsListContent, ItemStatus } from 'src/app/interfaces/views.interface';
import { DialogActionTypes } from 'src/app/notifications/dialogs/dialogs.enum';
import { NotificationContextAttributesService } from 'src/app/notifications/notifications-context-attributes.service';
import {
  CommunitiesBasic,
  CommunityBasic,
  CommunityCounters,
  CommunityStatus,
  PaginatableListOfBasicUsers,
  Post,
  PostBasic,
  Posts,
  Project,
  Projects,
} from 'src/app/swagger-api';
import { MaybeNull } from 'src/app/utils';
import { CardType } from 'src/app/views/cards/cards.types';
import { PostsService } from '../../posts/posts.service';
import { ProjectsService } from '../../projects/projects.service';
import { initialPaginationState, initialResolver } from '../../shared/social-initiative/social-initiative.helpers';
import { CommunityView } from '../communities-store';
import { ManageCommunityService } from '../manage-community.service';

@Component({
  selector: 'app-details-community',
  templateUrl: './details-community.component.html',
  styleUrls: ['./details-community.component.scss'],
  providers: [PostsService],
})
export class DetailsCommunityComponent implements OnInit, OnDestroy {
  @Input({ required: true }) public community: CommunityView;

  private manageCommunityService = inject(ManageCommunityService);
  private postsService = inject(PostsService);
  private projectsService = inject(ProjectsService);
  private sessionService = inject(SessionService);
  private router = inject(Router);
  private operationInfoService = inject(OperationInfoService);
  private modalAttributesService = inject(NotificationContextAttributesService);
  private translateService = inject(TranslateService);

  public readonly CardType: typeof CardType = CardType;
  public readonly LINK_COMMUNITIES: string = ProjectLinks.LINK_COMMUNITY_ALL;
  public readonly LINK_COMMUNITY_VIEW: string = this.manageCommunityService.SOCIAL_ENTITY_ROUTE_LINK;
  public readonly LINK_COMMUNITY_MANAGE: string = this.manageCommunityService.SOCIAL_ENTITY_MANAGE_ROUTE_LINK;
  public readonly PRIVATE: ItemStatus = ItemStatus.PRIVATE;
  public readonly EMPTY: ItemStatus = ItemStatus.EMPTY;

  public posts: PostBasic[] = [];
  public projects: Project[] = [];

  public page = 1;

  public isPrivate = false;
  public isAuthenticated = false;
  public isAdminSession = false;

  public resolver: PaginationResolver = initialResolver();

  public counters: CommunityCounters = {
    projects_count: 0,
    members_count: 0,
    posts_count: 0,
  };

  public pageSize = 10;
  public membersTotalCount = 0;
  public membersPageNumber = 1;

  public isSuggestedCommunitiesLoading = false;

  public suggestedCommunities: CommunityBasic[] = [];

  public membersTableConstructorData: TableConstructorData;

  public postsHelperContent: MaybeNull<ItemsListContent<Post>> = null;
  public projectsHelperContent: MaybeNull<ItemsListContent<Project>> = null;

  private subscriptions$: Subscription[] = [];

  private tabTypes: { [key: string]: () => void } = {
    community_posts: () => this.communityPostsHandler(),
    community_projects: () => this.communityProjectsHandler(),
    community_members: () => this.fetchCommunityMembers(),
  };

  public ngOnInit(): void {
    this.subscribeOnSessionChanges();
    this.subscribeCommunityProjects();
    this.subscribeCommunityPosts();
    this.subscribeOnCommunityMembersChanges();
    this.subscribeOnCounterChanges();

    this.subscriptions$.push(
      this.manageCommunityService.entityChanged$.subscribe((community: CommunityView) => {
        this.community = community;
        this.isPrivate = community.status === CommunityStatus.Closed;
        this.manageCommunityService.fetchRequestCommunityCounters(community.entity_id);

        this.setPostsHelperContent();
        this.setProjectsHelperContent();
        this.getRelatedCommunities();
      }),
    );
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public onTabClick($event: NgbNavChangeEvent<string>): void {
    if (this.tabTypes[$event.nextId]) {
      this.resolver.state = initialPaginationState();
      this.tabTypes[$event.nextId]();
    }
    this.manageCommunityService.fetchRequestCommunityCounters(this.community.entity_id);
    this.resetPaginationState();
  }

  public inviteToCommunity(): void {
    this.manageCommunityService.navigateToInviteToEntitySearchPage(this.community.entity_id);
  }

  public leaveCommunity(): void {
    this.operationInfoService.setupOperation(
      {
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
        successfulMessage: 'MANAGE_COMMUNITY_CONFIRM_LEAVE_HINT',
        confirmationDialog: {
          description: 'LEAVE_FROM_COMMUNITY_DESCRIPTION',
          title: 'LEAVE_FROM_COMMUNITY_TITLE',
          buttons: [
            this.modalAttributesService.createDialogButton('BTN_CLOSE_HINT'),
            this.modalAttributesService.createDialogButton(
              'BTN_LEAVE_HINT',
              'btn-primary',
              DialogActionTypes.CONFIRM_ACTION,
            ),
          ],
        },
      },
      () => {
        this.manageCommunityService.leaveEntity(
          this.community.entity_id,
          (this.membersPageNumber - 1) * this.pageSize,
          this.pageSize,
        );
      },
    );
  }

  public deleteMember(event: TableCellOnActionEvent): void {
    this.operationInfoService.setupOperation(
      {
        spinner: 'button',
        button: { id: 'btn-join' },
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
        successfulMessage: 'MANAGE_COMMUNITY_CONFIRM_USER_REMOVE_HINT',
        confirmationDialog: {
          description: 'REMOVE_USER_FROM_COMMUNITY_DESCRIPTION',
          title: 'REMOVE_USER_FROM_COMMUNITY_TITLE',
          buttons: [
            this.modalAttributesService.createDialogButton('BTN_CLOSE_HINT'),
            this.modalAttributesService.createDialogButton(
              'BTN_REMOVE_HINT',
              'btn-primary',
              DialogActionTypes.CONFIRM_ACTION,
            ),
          ],
        },
      },
      () => {
        this.manageCommunityService.removeMember(
          event.actorId,
          event.entityId,
          (this.membersPageNumber - 1) * this.pageSize,
          this.pageSize,
        );
      },
    );
  }

  public joinCommunity(): void {
    if (this.sessionService.isSessionActive()) {
      this.operationInfoService.setupOperation(
        {
          spinner: 'button',
          button: { id: 'btn-join' },
          blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
          successfulMessage:
            this.community.status === CommunityStatus.Open
              ? (this.translateService.instant('JOIN_A_PUBLIC_COMMUNITY_DIALOG_DESCRIPTION', {
                  community: this.community.title,
                }) as string)
              : 'JOIN_A_PRIVATE_COMMUNITY_DIALOG_DESCRIPTION',
        },
        () => {
          this.manageCommunityService.joinEntity(
            this.community.entity_id,
            (this.membersPageNumber - 1) * this.pageSize,
            this.pageSize,
          );
        },
      );
    } else {
      this.redirectToLoginPage();
    }
  }

  public redirectToLoginPage(): void {
    promiseWithCatch(
      this.router.navigate([ProjectLinks.LINK_LOGIN], {
        queryParams: { returnUrl: this.LINK_COMMUNITY_VIEW + '/' + this.community.entity_id },
      }),
    );
  }

  public onMembersPageChanged(page: number): void {
    this.membersPageNumber = page;
    this.fetchCommunityMembers();
  }

  private resetPaginationState(): void {
    this.membersTotalCount = 0;
    this.membersPageNumber = 1;
    this.pageSize = 10;
  }

  private subscribeOnCounterChanges(): void {
    this.subscriptions$.push(
      this.manageCommunityService.communityTabsCountersChanges.subscribe((counters: CommunityCounters) => {
        this.counters = counters;
      }),
    );
  }

  private setPostsHelperContent(): void {
    if (this.community.permissions.add_post) {
      this.postsHelperContent = {
        createItemInfo: {
          data: {
            community_id: {
              entity_id: this.community.entity_id,
              title: this.community.title,
            },
          },
          returnUrl: `${this.LINK_COMMUNITY_VIEW}/${this.community.entity_id}`,
          createLink: ProjectLinks.LINK_POSTS_CREATE,
          createItemTitle: 'BTN_CREATE_POST',
        },
      };
    }
  }

  private setProjectsHelperContent(): void {
    if (this.community.permissions.create_project) {
      this.projectsHelperContent = {
        createItemInfo: {
          data: {
            community_id: {
              entity_id: this.community.entity_id,
              title: this.community.title,
            },
          },
          returnUrl: `${this.LINK_COMMUNITY_VIEW}/${this.community.entity_id}`,
          createLink: ProjectLinks.LINK_PROJECTS_CREATE,
          createItemTitle: 'BTN_CREATE_PROJECT',
        },
      };
    }
  }

  private communityPostsHandler(): void {
    this.resolver.onPageChanged = (page: number) => {
      this.posts = [];
      this.resolver.state.pagination.currentPage = page;
      this.postsService.fetchCommunityPosts(this.community.entity_id, this.resolver.state);
    };
    this.postsService.fetchCommunityPosts(this.community.entity_id, this.resolver.state);
  }

  private communityProjectsHandler(): void {
    this.resolver.onPageChanged = (page: number) => {
      this.projects = [];
      this.resolver.state.pagination.currentPage = page;
      this.projectsService.fetchEntityRelatedItems(this.community.entity_id, this.resolver.state);
    };
    this.projectsService.fetchEntityRelatedItems(this.community.entity_id, this.resolver.state);
  }

  private fetchCommunityMembers(): void {
    this.manageCommunityService.fetchEntityMembers(
      this.community.entity_id,
      (this.membersPageNumber - 1) * this.pageSize,
      this.pageSize,
    );
  }

  private subscribeOnSessionChanges(): void {
    this.sessionService.sessionChanges.subscribe(() => {
      this.isAuthenticated = this.sessionService.isSessionActive();
      this.isAdminSession = this.sessionService.isAdminSession();
    });
    this.isAuthenticated = this.sessionService.isSessionActive();
    this.isAdminSession = this.sessionService.isAdminSession();
  }

  private subscribeCommunityProjects(): void {
    this.subscriptions$.push(
      this.projectsService.socialGroupChanges.subscribe((projects: Projects) => {
        this.projects = projects.items;
        this.resolver.state.pagination.totalItemsCount = projects.total_count;
      }),
    );
  }

  private subscribeCommunityPosts(): void {
    this.subscriptions$.push(
      this.postsService.postsChanges.subscribe((posts: Posts) => {
        this.posts = posts.items;
        this.resolver.state.pagination.totalItemsCount = posts.total_count;
      }),
    );
  }

  private getRelatedCommunities(): void {
    this.isSuggestedCommunitiesLoading = true;
    this.subscriptions$.push(
      this.manageCommunityService
        .getRelatedCommunities(this.community.entity_id, 10)
        .pipe(
          catchError(() => EMPTY),
          finalize(() => {
            this.isSuggestedCommunitiesLoading = false;
          }),
        )
        .subscribe((communities: CommunitiesBasic): void => {
          this.suggestedCommunities = communities.items;
        }),
    );
  }

  private subscribeOnCommunityMembersChanges(): void {
    this.subscriptions$.push(
      this.manageCommunityService.membersTableDataChanges.subscribe((tableData: TableConstructorData) => {
        this.membersTableConstructorData = tableData;
      }),

      this.manageCommunityService.membersChanged.subscribe((paginatableUsers: PaginatableListOfBasicUsers) => {
        this.membersTotalCount = paginatableUsers.total_count;
        if (paginatableUsers.items.length === 0 && this.membersPageNumber > 1) {
          this.membersPageNumber -= 1;
          this.fetchCommunityMembers();
        }
      }),
    );
  }
}
